/**
 * Font Studio — satu daftar untuk server dan browser.
 *
 * Kedua sisi menyisipkan font sebagai data URL, bukan URL: halaman render
 * memblokir semua request, dan CSP dokumen hanya mengizinkan `font-src data:`.
 * Font yang gagal termuat tidak menimbulkan error — teks diam-diam tertangkap
 * dengan font cadangan — jadi jalur muatnya sengaja dibuat sama persis.
 *
 * Yang berbeda hanya cara mendapatkan BYTE-nya: Worker mengimpor berkas woff2
 * sebagai ArrayBuffer (`apps/studio/api/render/fonts.ts`), browser mengambilnya
 * dari aset Vite (`apps/id/src/features/studio/lib/fonts.ts`). Keduanya wajib
 * memuat SEMUA entri di `FONT_FILES`; menambah font = tambah di sini dan di
 * kedua tempat itu.
 *
 * Subset `latin` mencakup U+0000–00FF, cukup untuk Indonesia dan Inggris
 * termasuk huruf beraksen umum. Lisensi ketiganya SIL OFL.
 */

export type FontFamily = 'Inter' | 'Playfair Display' | 'Bebas Neue'

export interface FontFile {
  family: FontFamily
  weight: number
  /** Path di dalam paket @fontsource, untuk kedua pemuat. */
  file: string
}

export const FONT_FILES: readonly FontFile[] = [
  { family: 'Inter', weight: 400, file: '@fontsource/inter/files/inter-latin-400-normal.woff2' },
  { family: 'Inter', weight: 600, file: '@fontsource/inter/files/inter-latin-600-normal.woff2' },
  { family: 'Inter', weight: 700, file: '@fontsource/inter/files/inter-latin-700-normal.woff2' },
  { family: 'Inter', weight: 800, file: '@fontsource/inter/files/inter-latin-800-normal.woff2' },
  {
    family: 'Playfair Display',
    weight: 700,
    file: '@fontsource/playfair-display/files/playfair-display-latin-700-normal.woff2',
  },
  {
    family: 'Playfair Display',
    weight: 800,
    file: '@fontsource/playfair-display/files/playfair-display-latin-800-normal.woff2',
  },
  { family: 'Bebas Neue', weight: 400, file: '@fontsource/bebas-neue/files/bebas-neue-latin-400-normal.woff2' },
]

/** Byte font dalam base64, dikunci `file` dari FONT_FILES. */
export type FontData = Record<string, string>

/** Blok `@font-face` untuk keluarga yang dipakai template. */
export function fontFaceCss(families: readonly FontFamily[], data: FontData): string {
  return FONT_FILES.filter((f) => families.includes(f.family))
    .map((f) => {
      const b64 = data[f.file]
      if (!b64) throw new Error(`Font belum dimuat: ${f.file}`)
      return (
        `@font-face{font-family:'${f.family}';font-weight:${f.weight};font-style:normal;` +
        `font-display:block;src:url(data:font/woff2;base64,${b64}) format('woff2')}`
      )
    })
    .join('\n')
}

/** ArrayBuffer -> base64 tanpa Buffer, jalan di Worker maupun browser. */
export function toBase64(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf)
  let s = ''
  // Dipotong per 32 KB: String.fromCharCode(...bytes) dengan array besar
  // melampaui batas jumlah argumen fungsi.
  for (let i = 0; i < bytes.length; i += 0x8000) {
    s += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  }
  return btoa(s)
}
